import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, Users, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { venueGamesService, VenueGame } from '@/services/venueGamesService';
import { GameRosterManager } from './GameRosterManager';
import { useToast } from '@/hooks/use-toast';

interface VenueGamesDashboardProps {
  venueId: string;
  showPendingOnly?: boolean;
}

export const VenueGamesDashboard: React.FC<VenueGamesDashboardProps> = ({ venueId, showPendingOnly = false }) => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;
  const { toast } = useToast();
  const [games, setGames] = useState<VenueGame[]>([]);
  const [pendingGames, setPendingGames] = useState<VenueGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);

  useEffect(() => {
    loadGames();
    loadPendingGames();
  }, [venueId]);

  const loadGames = async () => {
    try {
      console.log('🎯 DASHBOARD - Loading games for venue:', venueId);
      const upcomingGames = await venueGamesService.getUpcomingGames(venueId);
      console.log('🎯 DASHBOARD - Loaded upcoming games:', upcomingGames);
      setGames(upcomingGames);
    } catch (error) {
      console.error('❌ DASHBOARD - Error loading games:', error);
      toast({
        title: "Error loading games",
        description: "Failed to load upcoming games",
        variant: "destructive"
      });
    }
  };

  const loadPendingGames = async () => {
    try {
      console.log('🎯 DASHBOARD - Loading pending games for venue:', venueId);
      const { games: pending } = await venueGamesService.getPendingSubmissions(venueId);
      console.log('🎯 DASHBOARD - Loaded pending games with team data:', pending);
      
      setPendingGames(pending);
    } catch (error) {
      console.error('❌ DASHBOARD - Error loading pending games:', error);
      toast({
        title: "Error loading pending games",
        description: "Failed to load pending game requests",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const approveGame = async (gameId: string) => {
    try {
      await venueGamesService.updateGameStatus(gameId, 'approved', user?.id || '');
      toast({
        title: "Game approved",
        description: "The game has been approved and teams will be notified"
      });
      await loadGames();
      await loadPendingGames();
    } catch (error) {
      console.error('Error approving game:', error);
      toast({
        title: "Error",
        description: "Failed to approve game",
        variant: "destructive"
      });
    }
  };

  const rejectGame = async (gameId: string) => {
    try {
      await venueGamesService.updateGameStatus(gameId, 'rejected', user?.id || '');
      toast({
        title: "Game rejected",
        description: "The game has been rejected"
      });
      await loadPendingGames();
    } catch (error) {
      console.error('Error rejecting game:', error);
      toast({
        title: "Error",
        description: "Failed to reject game",
        variant: "destructive"
      });
    }
  };

  const getTeamName = (team: any): string => {
    if (!team) return 'TBD';
    if (Array.isArray(team)) return team[0]?.team_name || 'TBD';
    return team.team_name || 'TBD';
  };

  const getGameStatusBadge = (game: VenueGame) => {
    const homeTeamName = game.home_team?.team_name || 'TBD';
    const awayTeamName = game.away_team?.team_name || 'TBD';
    
    if (game.game_status === 'cancelled') {
      return <Badge variant="destructive">Cancelled</Badge>;
    }
    
    if (game.home_team_status === 'tbd' || game.away_team_status === 'tbd') {
      return <Badge variant="secondary">Waiting for Teams</Badge>;
    }
    
    if (game.home_team_status === 'accepted' && game.away_team_status === 'accepted') {
      return <Badge variant="default">Confirmed</Badge>;
    }
    
    if (game.home_team_status === 'assigned' || game.away_team_status === 'assigned') {
      return <Badge variant="outline">Awaiting Confirmation</Badge>;
    }
    
    return <Badge variant="secondary">Pending</Badge>;
  };

  const formatTime = (time: string) => {
    try {
      // Parse the time string (assuming it's in HH:MM format)
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      // Format to 12-hour time with CST
      const formattedTime = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      return `${formattedTime} CST`;
    } catch (error) {
      return time; // Fallback to original time if parsing fails
    }
  };

  const getTimeUntilGame = (gameDate: string, gameTime: string) => {
    const gameDateTime = new Date(`${gameDate}T${gameTime}`);
    const now = new Date();
    const diffMs = gameDateTime.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Game started';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays} days`;
    return `${diffHours} hours`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (selectedGameId) {
    const selectedGame = games.find(g => g.id === selectedGameId);
    if (selectedGame) {
      return (
        <div className="space-y-4">
          <Button 
            variant="outline" 
            onClick={() => setSelectedGameId(null)}
            className="mb-4"
          >
            ← Back to Games
          </Button>
          <GameRosterManager
            gameId={selectedGameId}
            homeTeamName={selectedGame.team?.team_name || 'TBD'}
            awayTeamName={selectedGame.away_team?.team_name || 'TBD'}
            gameDate={selectedGame.game_date}
            gameTime={selectedGame.game_time}
            isVenueOwner={true}
          />
        </div>
      );
    }
  }

  // If showPendingOnly is true, only show pending games
  if (showPendingOnly) {
    return (
      <div className="space-y-4">
        {pendingGames.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No pending game requests</p>
          </div>
        ) : (
          pendingGames.map((game) => (
            <Card key={game.id}>
              <CardHeader>
                <CardTitle className="text-lg">
                  {game.home_team?.team_name || 'Home Team'} vs {game.away_team?.team_name || 'Away Team'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{game.game_date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{formatTime(game.game_time)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Requested by: {game.posted_by_profile?.first_name} {game.posted_by_profile?.last_name}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium mb-2">Home Team</h4>
                    <p>{game.home_team?.team_name || 'TBD'}</p>
                    {!game.home_team?.team_name && game.game_code && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Registration Code: <span className="font-mono font-bold">{game.game_code}</span>
                      </div>
                    )}
                  </div>
                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium mb-2">Away Team</h4>
                    <p>{game.away_team?.team_name || 'TBD'}</p>
                    {!game.away_team?.team_name && game.game_code && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Registration Code: <span className="font-mono font-bold">{game.game_code}</span>
                      </div>
                    )}
                  </div>
                </div>

                {game.game_description && (
                  <p className="text-muted-foreground">{game.game_description}</p>
                )}

                <div className="flex gap-2">
                  <Button 
                    onClick={() => approveGame(game.id)}
                    className="bg-green-600 hover:bg-green-700 text-black"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => rejectGame(game.id)}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    );
  }

  return (
    <Tabs defaultValue="upcoming" className="space-y-6">
      <TabsList>
        <TabsTrigger value="upcoming">Upcoming Games</TabsTrigger>
        <TabsTrigger value="pending">
          Pending Approval
          {pendingGames.length > 0 && (
            <Badge variant="destructive" className="ml-2 text-xs">
              {pendingGames.length}
            </Badge>
          )}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="upcoming" className="space-y-4">
        {games.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">No upcoming games scheduled</p>
            </CardContent>
          </Card>
        ) : (
          games.map((game) => (
            <Card key={game.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {game.home_team?.team_name || 'Home Team'} vs {game.away_team?.team_name || 'Away Team'}
                  </CardTitle>
                  {getGameStatusBadge(game)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{game.game_date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{formatTime(game.game_time)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>Time until game: {getTimeUntilGame(game.game_date, game.game_time)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium mb-2">Home Team</h4>
                    <p>{game.home_team?.team_name || 'TBD'}</p>
                    {game.home_team_status === 'accepted' && (
                      <div className="flex items-center gap-1 text-green-600 text-sm mt-1">
                        <CheckCircle className="h-3 w-3" />
                        Confirmed
                      </div>
                    )}
                    {!game.home_team?.team_name && game.game_code && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Code: <span className="font-mono font-bold">{game.game_code}</span>
                      </div>
                    )}
                  </div>
                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium mb-2">Away Team</h4>
                    <p>{game.away_team?.team_name || 'TBD'}</p>
                    {game.away_team_status === 'accepted' && (
                      <div className="flex items-center gap-1 text-green-600 text-sm mt-1">
                        <CheckCircle className="h-3 w-3" />
                        Confirmed
                      </div>
                    )}
                    {!game.away_team?.team_name && game.game_code && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Code: <span className="font-mono font-bold">{game.game_code}</span>
                      </div>
                    )}
                  </div>
                </div>

                {game.game_status === 'cancelled' && game.cancellation_reason && (
                  <div className="border-l-4 border-red-500 bg-red-50 p-3">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-medium">Cancelled</span>
                    </div>
                    <p className="text-red-600 text-sm mt-1">{game.cancellation_reason}</p>
                  </div>
                )}

                {game.game_code && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-muted-foreground">Game Code for Team Registration:</p>
                    <p className="font-mono font-bold text-lg">{game.game_code}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedGameId(game.id)}
                  >
                    View Roster
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </TabsContent>

      <TabsContent value="pending" className="space-y-4">
        {pendingGames.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">No pending game requests</p>
            </CardContent>
          </Card>
        ) : (
          pendingGames.map((game) => (
            <Card key={game.id}>
              <CardHeader>
                <CardTitle className="text-lg">
                  {game.home_team?.team_name || 'Home Team'} vs {game.away_team?.team_name || 'Away Team'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{game.game_date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{formatTime(game.game_time)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Requested by: {game.posted_by_profile?.first_name} {game.posted_by_profile?.last_name}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium mb-2">Home Team</h4>
                    <p>{game.home_team?.team_name || 'TBD'}</p>
                    {!game.home_team?.team_name && game.game_code && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Registration Code: <span className="font-mono font-bold">{game.game_code}</span>
                      </div>
                    )}
                  </div>
                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium mb-2">Away Team</h4>
                    <p>{game.away_team?.team_name || 'TBD'}</p>
                    {!game.away_team?.team_name && game.game_code && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Registration Code: <span className="font-mono font-bold">{game.game_code}</span>
                      </div>
                    )}
                  </div>
                </div>

                {game.game_description && (
                  <p className="text-muted-foreground">{game.game_description}</p>
                )}

                <div className="flex gap-2">
                  <Button 
                    onClick={() => approveGame(game.id)}
                    className="bg-green-600 hover:bg-green-700 text-black"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => rejectGame(game.id)}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </TabsContent>
    </Tabs>
  );
};